import axios from 'axios';
import { Team } from './teamsApi';
import { getAuthHeader } from './authApi';

/**
 * Match status options
 */
export enum MatchStatus {
  SCHEDULED = 'SCHEDULED',
  LIVE = 'LIVE',
  COMPLETED = 'COMPLETED'
}

/**
 * Interface representing a Match
 */
export interface Match {
  id: string;
  source_id: string;
  round: string;
  matchday: number | null;
  home_team_id: number | null;
  away_team_id: number | null;
  home_team_placeholder: string;
  away_team_placeholder: string;
  home_team_score: number | null;
  away_team_score: number | null;
  result: string;
  winner: number | null;  // ID of the winning team, null for draws
  status: string;
  scorers: string[];
  home_team_yellow_cards: number;
  home_team_second_yellow_cards: number;
  home_team_red_cards: number;
  away_team_yellow_cards: number;
  away_team_second_yellow_cards: number;
  away_team_red_cards: number;
  match_date: string;
  knockout_next_match?: string; // ID of the next match in knockout stage
  knockout_next_match_home?: boolean;
  home_team?: Team;
  away_team?: Team;
  points?: number;
  score_success?: boolean;
  result_success?: boolean;
  user_bet_home_score?: number | null;
  user_bet_away_score?: number | null;
  user_bet_winner?: number | null;
  winner_id?: number | null; // User's bet on which team qualifies in knockout matches
  group_id: number | null;
  is_last_groups_match?: boolean;
  bettable: boolean;
}

/**
 * Interface for match data coming from forms
 */
export interface MatchFormData {
  id: string;
  source_id: string;
  round: string;
  matchday: number | null;
  home_team_id: number | null;
  away_team_id: number | null;
  home_team_placeholder: string;
  away_team_placeholder: string;
  home_team_score?: number | null;
  away_team_score?: number | null;
  result?: string;
  winner?: number | null;  // ID of the winning team, null for draws
  status: string;
  scorers?: string[];
  home_team_yellow_cards?: number;
  home_team_second_yellow_cards?: number;
  home_team_red_cards?: number;
  away_team_yellow_cards?: number;
  away_team_second_yellow_cards?: number;
  away_team_red_cards?: number;
  match_date: string;
  group_id: number | null;
  knockout_next_match?: string | null;
  knockout_next_match_home?: boolean;
  is_last_groups_match?: boolean;
  bettable: boolean;
}

/**
 * Fetches all matches from the API
 * @returns Array of matches
 */
export const getMatches = async (): Promise<Match[]> => {
  try {
    const response = await axios.get('/api/matches', {
      headers: getAuthHeader()
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching matches:', error);
    return [];
  }
};

/**
 * Fetches a specific match by ID
 * @param id - Match ID
 * @returns Match object
 */
export const getMatch = async (id: string): Promise<Match> => {
  try {
    const response = await axios.get<Match>(`/api/matches/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching match ${id}:`, error);
    throw error;
  }
};

/**
 * Creates a new match
 * @param matchData - Raw match data from form
 * @returns Created match
 */
export const createMatch = async (matchData: MatchFormData): Promise<Match> => {
  try {
    const response = await axios.post<Match>('/api/matches', matchData);
    return response.data;
  } catch (error) {
    console.error('Error creating match:', error);
    throw error;
  }
};

/**
 * Updates an existing match
 * @param id - Match ID
 * @param matchData - Raw match data from form
 * @returns Updated match
 */
export const updateMatch = async (id: string, matchData: MatchFormData): Promise<Match> => {
  try {
    const response = await axios.put<Match>(`/api/matches/${id}`, matchData);
    return response.data;
  } catch (error) {
    console.error(`Error updating match ${id}:`, error);
    throw error;
  }
};

/**
 * Deletes a match
 * @param id - Match ID to delete
 * @returns Response data
 */
export const deleteMatch = async (id: string): Promise<any> => {
  try {
    const response = await axios.delete(`/api/matches/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting match ${id}:`, error);
    throw error;
  }
};

/**
 * Get all user match bets
 */
export const getUserMatchBets = async (): Promise<Match[]> => {
  try {
    const response = await axios.get('/api/user/matches', {
      headers: getAuthHeader()
    });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching user match bets:', error);
    return [];
  }
};

/**
 * Get all knockout matches
 */
export const getKnockoutMatches = async (): Promise<Match[]> => {
  try {
    const response = await axios.get('/api/knockout', {
      headers: getAuthHeader()
    });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching knockout matches:', error);
    return [];
  }
};

/**
 * Place a bet on a knockout match
 */
export const placeKnockoutBet = async (
  matchId: string, 
  homeScore: number, 
  awayScore: number, 
  qualifierTeamId?: number | undefined
): Promise<Match> => {
  try {
    const response = await axios.post(
      `/api/user/matches/bet`, 
      {
        home_score: homeScore,
        away_score: awayScore,
        winner_id: qualifierTeamId,
        match_id: matchId
      },
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error placing bet on match ${matchId}:`, error);
    throw error;
  }
};

export interface OtherUserBet {
  id: number;
  username: string;
  avatar?: string;
  home_score: number;
  away_score: number;
  winner_team_id?: number;
  points: number;
  score_success?: boolean;
  result_success?: boolean;
}

export const getOtherUsersBets = async (matchId: string): Promise<OtherUserBet[]> => {
  try {
    const response = await axios.get(`/api/matches/${matchId}/other-bets`, {
      headers: getAuthHeader()
    });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching other users bets:', error);
    return [];
  }
}; 

/**
 * Get score statistics for a match
 */
export const getMatchScoreStatistics = async (matchId: string): Promise<{score: string, count: number}[]> => {
  try {
    const response = await axios.get(`/api/matches/${matchId}/score-statistics`, {
      headers: getAuthHeader()
    });
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching score statistics for match ${matchId}:`, error);
    return [];
  }
};

/**
 * Get winner statistics for a match
 */
export const getMatchWinnerStatistics = async (matchId: string): Promise<{winner: number | null, count: number}[]> => {
  try {
    const response = await axios.get(`/api/matches/${matchId}/winner-statistics`, {
      headers: getAuthHeader()
    });
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching winner statistics for match ${matchId}:`, error);
    return [];
  }
};

/**
 * Get group standings for a specific group
 */
export const getGroupStandings = async (groupId: number): Promise<any[]> => {
  try {
    const response = await axios.get(`/api/groups/${groupId}/standings`, {
      headers: getAuthHeader()
    });
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching group standings for group ${groupId}:`, error);
    return [];
  }
}; 